import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Collapse,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import {
  Dashboard,
  CloudUpload,
  People,
  CalendarMonth,
  Assessment,
  Settings,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  EventNote,
  Upload,
  PersonAdd,
  Schedule,
  ViewList,
  BarChart,
  AccountCircle,
  Notifications as NotificationsIcon,
  ExitToApp,
  NewReleases,
  Logout as LogoutIcon,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/authService';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  badge?: number;
  children?: MenuItem[];
  requiredPermission?: string;
  isNew?: boolean;
  isComingSoon?: boolean;
}

interface NavigationSidebarProps {
  open: boolean;
  onToggle: () => void;
}

const menuItems: MenuItem[] = [
  // ダッシュボードページを非表示
  // {
  //   id: 'dashboard',
  //   label: 'ダッシュボード',
  //   icon: <Dashboard />,
  //   path: '/dashboard',
  // },
  {
    id: 'appointments',
    label: '予約管理',
    icon: <EventNote />,
    children: [
      {
        id: 'appointment-list',
        label: '診療予約データ一覧',
        icon: <ViewList />,
        path: '/appointments/list',
      },
      {
        id: 'out-of-hours-list',
        label: '指定時間外予約一覧',
        icon: <AccessTime />,
        path: '/appointments/out-of-hours',
      },
      {
        id: 'appointment-upload',
        label: '診療予約CSVアップロード',
        icon: <CloudUpload />,
        path: '/appointments/upload',
      },
      {
        id: 'appointment-calendar',
        label: '予約カレンダービュー',
        icon: <CalendarMonth />,
        path: '/appointments/calendar',
        isComingSoon: true,
      },
    ],
  },
  {
    id: 'doctors',
    label: '医師管理',
    icon: <People />,
    children: [
      {
        id: 'doctor-list',
        label: '医師一覧・プロフィール',
        icon: <PersonAdd />,
        path: '/doctors/list',
        requiredPermission: 'admin',
      },
      {
        id: 'doctor-shifts',
        label: '医師シフト管理',
        icon: <Schedule />,
        path: '/doctors/shifts',
      },
      {
        id: 'shift-rules',
        label: '医師シフトルール',
        icon: <EventNote />,
        path: '/doctors/shift-rules',
        requiredPermission: 'admin',
      },
      {
        id: 'shift-manager',
        label: '月次シフト管理',
        icon: <CalendarMonth />,
        path: '/doctors/shifts/manager',
        requiredPermission: 'admin',
      },
      {
        id: 'reservation-slots',
        label: '解放枠管理',
        icon: <NewReleases />,
        path: '/doctors/reservation-slots',
        requiredPermission: 'admin',
      },
      {
        id: 'shift-upload',
        label: '医師シフトCSVアップロード',
        icon: <Upload />,
        path: '/doctors/shift-upload',
      },
      {
        id: 'shift-calendar',
        label: 'シフトカレンダー',
        icon: <CalendarMonth />,
        path: '/doctors/shift-calendar',
        isComingSoon: true,
      },
    ],
  },
  {
    id: 'user-management',
    label: 'ユーザー管理',
    icon: <AccountCircle />,
    path: '/settings/users',
    requiredPermission: 'admin',
  },
  // レポート・分析ページを非表示
  // {
  //   id: 'reports',
  //   label: 'レポート・分析',
  //   icon: <Assessment />,
  //   children: [
  //     {
  //       id: 'appointment-stats',
  //       label: '予約統計',
  //       icon: <BarChart />,
  //       path: '/reports/appointments',
  //     },
  //     {
  //       id: 'doctor-utilization',
  //       label: '医師稼働率',
  //       icon: <BarChart />,
  //       path: '/reports/utilization',
  //     },
  //     {
  //       id: 'monthly-report',
  //       label: '月次レポート',
  //       icon: <Assessment />,
  //       path: '/reports/monthly',
  //       isComingSoon: true,
  //     },
  //   ],
  // },
  // システム設定ページを非表示
  // {
  //   id: 'settings',
  //   label: 'システム設定',
  //   icon: <Settings />,
  //   children: [
  //     {
  //       id: 'account',
  //       label: 'アカウント管理',
  //       icon: <AccountCircle />,
  //       path: '/settings/account',
  //     },
  //     {
  //       id: 'notifications',
  //       label: '通知設定',
  //       icon: <NotificationsIcon />,
  //       path: '/settings/notifications',
  //     },
  //     {
  //       id: 'export',
  //       label: 'データエクスポート',
  //       icon: <ExitToApp />,
  //       path: '/settings/export',
  //     },
  //   ],
  // },
];

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedItems, setExpandedItems] = useState<string[]>(['appointments', 'doctors']);
  const { user, logout } = useAuth();
  const [userManagementVisible, setUserManagementVisible] = useState(false);

  useEffect(() => {
    authService.getConfig().then(config => setUserManagementVisible(config.user_management_visible))
      .catch(() => setUserManagementVisible(false));
  }, []);

  const drawerWidth = open ? 280 : 72;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.isComingSoon) {
      // 開発中の機能は通知表示
      return;
    }

    if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onToggle();
      }
    } else if (item.children) {
      setExpandedItems((prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id]
      );
    }
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some((child) => child.path === location.pathname);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    if (item.id === 'user-management' && !userManagementVisible) return null;
    // 権限チェック: requiredPermissionが設定されている場合、ユーザーのロールと一致しなければ非表示
    if (item.requiredPermission && user?.role !== item.requiredPermission) {
      return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = isItemActive(item);

    return (
      <React.Fragment key={item.id}>
        <ListItem
          disablePadding
          sx={{
            display: 'block',
            px: depth > 0 ? 1 : 0,
          }}
        >
          <ListItemButton
            onClick={() => handleItemClick(item)}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              pl: depth > 0 ? 4 : 2.5,
              borderRadius: '4px',
              mx: 1,
              mb: 0.5,
              backgroundColor: isActive
                ? alpha(theme.palette.primary.main, 0.12)
                : 'transparent',
              color: isActive ? theme.palette.primary.main : 'inherit',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              ...(item.isComingSoon && {
                opacity: 0.6,
                cursor: 'not-allowed',
              }),
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isActive ? theme.palette.primary.main : 'inherit',
              }}
            >
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: open ? 1 : 0,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </Typography>
                  {item.isNew && open && (
                    <Chip
                      label="New"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {item.isComingSoon && open && (
                    <Chip
                      label="近日公開"
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
              sx={{ opacity: open ? 1 : 0 }}
            />
            {hasChildren && open && (
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ExpandMore />
              </motion.div>
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '6px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '1.5rem',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            M
          </Box>
          {open && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" fontWeight="bold">
                MEDERI
              </Typography>
              <Typography variant="caption" color="text.secondary">
                管理システム
              </Typography>
            </motion.div>
          )}
        </Box>
        {!isMobile && (
          <IconButton onClick={onToggle} size="small">
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        )}
      </Box>

      {/* メニューリスト */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <List>{menuItems.map((item) => renderMenuItem(item))}</List>
      </Box>

      {/* フッター */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* ユーザー情報 */}
        {user && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
              }}
            >
              <Avatar
                sx={{
                  width: open ? 40 : 32,
                  height: open ? 40 : 32,
                  bgcolor: theme.palette.primary.main,
                  fontSize: open ? '1rem' : '0.8rem',
                }}
              >
                {user.name ? user.name.charAt(0) : 'U'}
              </Avatar>
              {open && (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* ログアウトボタン */}
            <Tooltip title={!open ? 'ログアウト' : ''} placement="right">
              <Button
                fullWidth={open}
                size="small"
                variant={open ? 'outlined' : 'text'}
                color="primary"
                onClick={handleLogout}
                startIcon={open ? <LogoutIcon /> : undefined}
                sx={{
                  minWidth: open ? 'auto' : 40,
                  height: 36,
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 2 : 0,
                }}
              >
                {open ? 'ログアウト' : <LogoutIcon fontSize="small" />}
              </Button>
            </Tooltip>
          </Box>
        )}
        {/* バージョン情報 */}
        {open && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Version 1.0.0
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              © 2024 MEDERI Inc.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={isMobile ? onToggle : undefined}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          border: 'none',
          boxShadow: `2px 0 8px ${alpha(theme.palette.common.black, 0.1)}`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default NavigationSidebar;
